import { type ReactNode } from 'react'
import cx from 'classnames'
import { copy, isSupported } from 'clipbrd'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { SplitView, SplitViewMain, SplitViewSide } from '@/components/SplitView'
import { TabBar, TabItem } from '@/components/TabBar'
import type { UserDataType } from '@/features/iam/users/UserData'
import { match } from '@/utils/pattern-match-util'
import { getUserDataTypeDisplayName } from '@/features/user-management/user-data-type-display'
import styles from './UserDetails.module.css'
import { type UserDetailsTab } from '@/features/user-management/UserDetails/UserDetailsViewModel'

export interface UserDetailsProps {
  isSidebarOpen: boolean
  header: ReactNode
  tabs: ReactNode
  information: ReactNode
  /**
   * The content of the selected tab.
   */
  tabContent: ReactNode
  dialogs: ReactNode
  sidebar: ReactNode
}

export function UserDetails({
  isSidebarOpen,
  header,
  tabs,
  information,
  tabContent,
  dialogs,
  sidebar,
}: UserDetailsProps) {
  return (
    <div className={styles.root}>
      <div className={cx(styles.main, isSidebarOpen && styles.narrow)}>
        {header}
        {tabs}
        <SplitView flexChild>
          <SplitViewSide narrow>
            <div className={styles.userInfoRoot}>
              <div className={styles.userInfoScrollContainer}>
                {information}
              </div>
            </div>
          </SplitViewSide>
          <SplitViewMain>
            <div className={styles.userTabRoot}>{tabContent}</div>
          </SplitViewMain>
        </SplitView>
        {dialogs}
      </div>
      <div className={cx(styles.sidebar, isSidebarOpen && styles.show)}>
        {sidebar}
      </div>
    </div>
  )
}

export interface UserDetailsHeaderProps {
  avatar: string | null
  displayName: string
  userDataType: UserDataType
  email: string
  canCreateJob: boolean
  canEditUserInformation: boolean
  onAddToTeam: () => void
  onAddToWorkspace: () => void
  onCreateJob: () => void
  onEditUserInformation: () => void
}

export function UserDetailsHeader({
  avatar,
  displayName,
  userDataType,
  email,
  canCreateJob,
  canEditUserInformation,
  onAddToTeam,
  onAddToWorkspace,
  onCreateJob,
  onEditUserInformation,
}: UserDetailsHeaderProps) {
  const copyEmailToClipboard = (email: string) => {
    if (isSupported()) {
      copy(email)
    }
  }

  return (
    <div className={styles.header}>
      <div className={styles.headerInfo}>
        <Avatar size={48} subject={{ avatar }} />
        <div className={styles.headerInfoText}>
          <h1 className={styles.headerInfoTitle}>{displayName}</h1>
          <div className={styles.headerInfoSubtitle}>
            <h5
              className={cx(
                styles.headerInfoUserType,
                match(userDataType, {
                  User: styles.user,
                  Pro: styles.pro,
                  Admin: styles.admin,
                  CallAgent: styles.callAgent,
                }),
              )}
            >
              {getUserDataTypeDisplayName(userDataType)}
            </h5>
            <span className={styles.headerInfoDivider}>|</span>
            <p className={styles.headerInfoEmail}>{email}</p>
            <IconButton
              name="copy"
              label="Copy email"
              className={styles.headerInfoCopyIcon}
              size={16}
              inline
              onPress={() => copyEmailToClipboard(email)}
            />
          </div>
        </div>
      </div>
      <div className={styles.headerActions}>
        <Dropdown
          autoDismiss
          position="bottom-end"
          button={
            <Button
              label="Actions"
              variant="secondary"
              className={styles.actionsButton}
              iconRight="dropdown"
              iconRightFlush
              testId="user-details-actions"
            />
          }
        >
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownItem
            onPress={onAddToTeam}
            buttonTestId="user-details-actions-add-to-team"
          >
            Add user to team
          </DropdownItem>
          <DropdownItem
            onPress={onAddToWorkspace}
            buttonTestId="user-details-actions-add-to-workspace"
          >
            Add user to workspace
          </DropdownItem>
          {canCreateJob && (
            <DropdownItem
              onPress={onCreateJob}
              buttonTestId="user-details-actions-create-job"
            >
              Create job for user
            </DropdownItem>
          )}
          {canEditUserInformation && (
            <DropdownItem
              onPress={onEditUserInformation}
              buttonTestId="user-details-actions-edit-user-information"
            >
              Edit user information
            </DropdownItem>
          )}
        </Dropdown>
      </div>
    </div>
  )
}

export interface UserDetailsTabsProps {
  tabs: UserDetailsTab[]
  currentTab: UserDetailsTab
  onTabChange: (tab: UserDetailsTab) => void
}

export function UserDetailsTabs({
  tabs,
  currentTab,
  onTabChange,
}: UserDetailsTabsProps) {
  return (
    <div className={styles.tabs}>
      <TabBar>
        {tabs.map((tab) => (
          <TabItem
            key={tab}
            active={currentTab === tab}
            onPress={() => onTabChange(tab)}
            testId={`user-details-tab-${tab.toLowerCase()}`}
          >
            {tab}
          </TabItem>
        ))}
      </TabBar>
    </div>
  )
}
